// Third Party
import { useMutation } from "@tanstack/react-query";

// UI
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Spinner } from "./ui/spinner";

// Icons
import { FilePieChartIcon, FileUp, X } from "lucide-react";
import { handleUploadFile } from "@/services/presign-url";
import { cn } from "@/lib/utils";
import { S3_BUCKET_LINK } from "./constants";

interface FileUploadProps {
	onFileUpload: (url: string) => void;
	currentFile?: string;
	isSubmitting?: boolean;
	required?: boolean;
}

export function FileUpload({
	onFileUpload,
	currentFile,
	isSubmitting,
	required = false,
}: FileUploadProps) {
	const { toast } = useToast();

	const uploadMutation = useMutation({
		mutationFn: handleUploadFile,
		onSuccess: (filename) => {
			// console.log("Upload successful, filename:", filename);

			onFileUpload(filename);

			toast({
				title: "ສຳເລັດ",
				description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ",
				variant: "success",
			});
		},
		onError: (error) => {
			console.error("Upload failed:", error);

			const fileInput = document.getElementById("fileUrl") as HTMLInputElement;
			if (fileInput) fileInput.value = "";

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
				variant: "destructive",
			});
		},
	});



	return (
		<div className="flex flex-col gap-2">
			<label className="text-sm font-medium text-gray-700">ເອກະສານຕິດຄັດ {required ? <span className="text-red-500">*</span> : null}</label>

			{currentFile ? (
				<div className="relative p-4 bg-green-50 rounded-lg border-2 border-green-200 transition-all hover:border-green-300">
					<div className="flex gap-3 items-center">
						<FilePieChartIcon className="flex-shrink-0 w-8 h-8 text-green-500" />
						<div className="flex-1 min-w-0">
							<p className="max-w-full text-sm font-medium text-gray-700 truncate">
								{/* {currentFile.split('/').pop()?.replace(/^[0-9a-f-]+-/, "Attached File - ")} */}
								ເອກະສານຕິດຄັດ {required ? <span className="text-red-500">*</span> : null}
							</p>
							<p className="text-xs text-gray-500">
								ຄລິກທີ່ລິ້ງເພື່ອດາວໂຫຼດເອກະສານ
							</p>
						</div>
						<Button
							variant="ghost"
							size="sm"
							className="relative z-50 flex-shrink-0 hover:bg-red-100 hover:text-red-600"
							onClick={() => onFileUpload("")}
						>
							<X className="w-4 h-4" />
						</Button>
					</div>
					<div
						className="absolute inset-0 z-40 opacity-0 transition-all hover:opacity-100"
					>
						<a
							className="flex justify-center items-center w-full h-full rounded-lg hover:bg-green-100/50"
							target="_blank"
							href={S3_BUCKET_LINK + currentFile}
							download={S3_BUCKET_LINK + currentFile}
							rel="noreferrer"
						>
							<span className="flex gap-2 items-center px-4 py-2 bg-white rounded-full shadow-sm">
								<FileUp className="w-4 h-4" />
								ດາວໂຫຼດເອກະສານ
							</span>
						</a>
					</div>
				</div>
			) : (
				<div className="relative">
					<input
						type="file"
						id="docFile"
						name="docFile"
						accept=".pdf,.doc,.docx,.xls,.xlsx"
						onChange={(e) => {
							if (e.target.files) {
								uploadMutation.mutate(e);
							}
						}}
						className="hidden"
						required={required}
					/>
					<div
						onClick={() => document.getElementById("docFile")?.click()}
						className={cn(
							"flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-lg cursor-pointer transition-all",
							"hover:border-green-400 hover:bg-green-50",
							isSubmitting || uploadMutation.isPending ? "opacity-50 cursor-not-allowed" : "border-gray-300"
						)}
					>
						{uploadMutation.isPending ? (
							<>
								<Spinner className="mb-2 w-8 h-8 text-green-500" />
								<p className="text-sm text-gray-600">ກຳລັງອັບໂຫຼດ...</p>
							</>
						) : (
							<>
								<FileUp className="mb-2 w-8 h-8 text-green-500" />
								<p className="text-sm font-medium text-gray-700">ຄລິກເພື່ອອັບໂຫຼດເອກະສານ</p>
								<p className="mt-1 text-xs text-gray-500">
									ຮອງຮັບໄຟລ໌: PDF, Word, Excel
								</p>
							</>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
